import { z } from "zod";
import { createDocumentFormFields } from "@/components/invoices/documentFormFields";

/** The translator for `consultantDraft.validation`. */
export type ValidationTranslator = (
  key: "amountRequired" | "amountPositive" | "currencyRequired" | "categoryRequired" | "dueDateRequired" | "nameRequired" | "companyRequired",
) => string;

export const DOCUMENT_TYPES = ["payable", "receivable", "receipt", "expense", "sales_report"] as const;
export const PAYMENT_METHODS = ["card", "cash", "bank_transfer", "salary_adjustment"] as const;

/** The draft form's schema, with its validation messages in the language the app shows. */
export function createDraftFormSchema(t: ValidationTranslator) {
  const participant = { name: z.string().min(1, t("nameRequired")), company: z.string().min(1, t("companyRequired")) };
  // Mirrors `Participant`: an internal participant is always tied to an employee.
  const participantSchema = z.discriminatedUnion("source", [
    z.object({ source: z.literal("employee"), employee_id: z.string().uuid(), ...participant }),
    z.object({ source: z.literal("external"), ...participant }),
  ]);

  return z.object({
    documentType: z.enum(DOCUMENT_TYPES).optional(),
    ...createDocumentFormFields(t),
    remittanceInformation: z.string().optional(),
    vendorIban: z.string().optional(),
    vendorBankgiro: z.string().optional(),
    vendorPlusgiro: z.string().optional(),
    vendorAccountNumber: z.string().optional(),
    vendorClearingNumber: z.string().optional(),
    bankBic: z.string().optional(),
    bankName: z.string().optional(),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    expenseCategory: z.string().optional(),
    participants: z.array(participantSchema).optional(),
    // Sales report specific fields
    reportDate: z.string().optional(),
    totalGrossSales: z.coerce.number().optional(),
    totalReturns: z.coerce.number().optional(),
    totalNetSales: z.coerce.number().optional(),
    vatSummary: z.string().optional(), // JSON string for editing
    paymentMethods: z.string().optional(), // JSON string for editing
    salesCategories: z.string().optional(), // JSON string for editing
  }).superRefine((data, ctx) => {
    if ((data.documentType === "receipt" || data.documentType === "expense") && !data.expenseCategory) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("categoryRequired"),
        path: ["expenseCategory"],
      });
    }
    // FIN-1100: the draft carries no placeholder, so a missing due date is asked for here.
    if ((data.documentType === "payable" || data.documentType === "receivable") && !data.dueDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("dueDateRequired"),
        path: ["dueDate"],
      });
    }
  });
}

type DraftFormSchema = ReturnType<typeof createDraftFormSchema>;

/** What the form holds while it is edited (numbers are still raw input) and what it submits. */
export type DraftFormInput = z.input<DraftFormSchema>;
export type DraftFormValues = z.output<DraftFormSchema>;
